import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAllRestaurants, getRestaurantRatingSummary } from '../services/restaurantService';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import Pagination from '../components/Pagination';
import Carousel from 'react-bootstrap/Carousel';
import './Home.css';

const Home = () => {
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ratingsMap, setRatingsMap] = useState({}); // { [id]: { average, count } }
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 9, // 3x3 grid
    totalCount: 0,
    totalPages: 0,
  });
  const navigate = useNavigate();

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    fetchRestaurants();
  }, [pagination.currentPage, debouncedSearch]);

  const fetchRestaurants = async () => {
    try {
      setLoading(true);
      const params = {
        pageNumber: pagination.currentPage,
        pageSize: pagination.pageSize,
        ...(debouncedSearch && { name: debouncedSearch }),
      };
      const data = await getAllRestaurants(params);
      // Backend returns { results: [], totalCount: number }
      const list = data.results || [];
      setRestaurants(list);

      // Update pagination info
      setPagination(prev => ({
        ...prev,
        totalCount: data.totalCount || 0,
        totalPages: Math.ceil((data.totalCount || 0) / prev.pageSize),
      }));

      // fetch rating summaries in parallel (best-effort)
      const summaries = await Promise.all(
        list.map(r => getRestaurantRatingSummary(r.id).catch(() => ({ average: 0, count: 0 })))
      );
      const map = {};
      list.forEach((r, idx) => {
        map[r.id] = summaries[idx];
      });
      setRatingsMap(map);
      setError('');
    } catch (err) {
      setError('Failed to load restaurants');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, currentPage: newPage }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
  };

  // Reset to first page when debounced search changes
  useEffect(() => {
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  }, [debouncedSearch]);

  const clearSearch = () => {
    setSearchQuery('');
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  return (
    <div className="home-container">
      <Navbar />

      {/* Carousel Section */}
      <div className="carousel-section">
        <Carousel fade interval={4000} pause={false} className="hero-carousel">
          <Carousel.Item>
            <img
              className="d-block w-100 carousel-image"
              src="/Carousel/Carousel1.jpg"
              alt="Fine Dining Experience"
            />
            <Carousel.Caption className="carousel-custom-caption">
              <div className="restaurant-count">
                <div className="count-number">{pagination.totalCount}+</div>
                <div className="count-label">Amazing Restaurants</div>
              </div>
            </Carousel.Caption>
          </Carousel.Item>
          <Carousel.Item>
            <img
              className="d-block w-100 carousel-image"
              src="/Carousel/Carousel2.jpg"
              alt="Culinary Excellence"
            />
            <Carousel.Caption className="carousel-custom-caption">
              <div className="restaurant-count">
                <div className="count-number">{pagination.totalCount}+</div>
                <div className="count-label">Amazing Restaurants</div>
              </div>
            </Carousel.Caption>
          </Carousel.Item>
          <Carousel.Item>
            <img
              className="d-block w-100 carousel-image"
              src="/Carousel/Carousel3.jpg"
              alt="Unforgettable Moments"
            />
            <Carousel.Caption className="carousel-custom-caption">
              <div className="restaurant-count">
                <div className="count-number">{pagination.totalCount}+</div>
                <div className="count-label">Amazing Restaurants</div>
              </div>
            </Carousel.Caption>
          </Carousel.Item>
        </Carousel>
      </div>

      {/* Membership Banner - Temporarily hidden until feature is complete */}
      {/* <div className="membership-banner">
        <div className="container">
          <div className="membership-banner-content">
            <div className="membership-icon">
              <i className="bi bi-award-fill"></i>
            </div>
            <div className="membership-text">
              <h2>Join Our Exclusive Membership</h2>
              <p>Get priority reservations, exclusive discounts, and special perks at all our restaurants</p>
            </div>
            <button className="btn-membership" onClick={() => navigate('/membership')}>
              Learn More
              <i className="bi bi-arrow-right ms-2"></i>
            </button>
          </div>
        </div>
      </div> */}

      <div className="container py-3">
        <div className="section-header-home">
          <h2 className="section-title">Our Restaurants</h2>
          <div className="search-container">
            <div className="search-input-wrapper">
              <i className="bi bi-search search-icon"></i>
              <input
                type="text"
                className="search-input"
                placeholder="Search restaurants..."
                value={searchQuery}
                onChange={handleSearchChange}
              />
              {searchQuery && (
                <button className="search-clear-btn" onClick={clearSearch}>
                  <i className="bi bi-x-lg"></i>
                </button>
              )}
            </div>
          </div>
        </div>

        {error && (
          <div className="error-alert" role="alert">
            {error}
          </div>
        )}

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-success" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : (
          <div className="row g-4">
            {restaurants.length === 0 ? (
              <div className="col-12 text-center py-5">
                <div className="empty-state">
                  <i className="bi bi-shop empty-icon"></i>
                  <h3>No restaurants available at the moment</h3>
                  <p>Please check back later!</p>
                </div>
              </div>
            ) : (
              restaurants.map((restaurant) => (
                <div className="col-md-6 col-lg-4" key={restaurant.id}>
                  <div className="restaurant-card" onClick={() => navigate(`/restaurant/${restaurant.id}`)}>
                    <div className="card-image-wrapper">
                      {restaurant.imageUrl ? (
                        <img
                          src={`${process.env.REACT_APP_BACKEND_URL}${restaurant.imageUrl}`}
                          className="card-img"
                          alt={restaurant.name}
                        />
                      ) : (
                        <div className="card-img-placeholder">
                          <i className="bi bi-image"></i>
                        </div>
                      )}
                      {restaurant.category && (
                        <div className="card-badge">{restaurant.category}</div>
                      )}
                    </div>

                    <div className="card-content">
                      <h5 className="card-title">{restaurant.name}</h5>

                      {restaurant.location && (
                        <div className="card-location">
                          <i className="bi bi-geo-alt-fill"></i>
                          <span>{restaurant.location}</span>
                        </div>
                      )}

                      <div className="card-rating">
                        <div className="stars">
                          {Array.from({ length: 5 }).map((_, i) => {
                            const rating = ratingsMap[restaurant.id]?.count > 0
                              ? ratingsMap[restaurant.id].average
                              : 4.5;
                            const starPosition = i + 1;
                            let starClass = 'bi-star'; // empty star
                            if (rating >= starPosition) {
                              starClass = 'bi-star-fill'; // full star
                            } else if (rating >= starPosition - 0.5) {
                              starClass = 'bi-star-half'; // half star
                            }
                            return (
                              <i
                                key={i}
                                className={`bi ${starClass}`}
                              />
                            );
                          })}
                        </div>
                        <span className="rating-text">
                          {ratingsMap[restaurant.id]?.count > 0
                            ? `${ratingsMap[restaurant.id].average.toFixed(1)} (${ratingsMap[restaurant.id].count})`
                            : '4.5'}
                        </span>
                      </div>

                      {restaurant.description && (
                        <p className="card-description">
                          {restaurant.description.length > 100
                            ? `${restaurant.description.substring(0, 100)}...`
                            : restaurant.description}
                        </p>
                      )}

                      <button className="card-btn">
                        <span>Book Now</span>
                        <i className="bi bi-arrow-right"></i>
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Pagination */}
        {!loading && restaurants.length > 0 && (
          <Pagination
            currentPage={pagination.currentPage}
            totalPages={pagination.totalPages}
            totalItems={pagination.totalCount}
            itemsPerPage={pagination.pageSize}
            onPageChange={handlePageChange}
          />
        )}
      </div>

      <Footer />
    </div>
  );
};

export default Home;
